import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useStmAnalyticsEvent } from '@dni-analysis'
import { StmSectionName } from '@dni-analysis/types/fastpass-ga4-events'
import { useDialog } from '@dni-hooks/useDialog'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type CrossBrandMallHomeCampaign, CrossBrandMallNewArrivalImageDeviceType } from '@hooks-api/types/dataModel/cross-brand-mall'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Image } from '@pets-ui/Image/Image'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import * as stylex from '@stylexjs/stylex'
import { FpB2bProductCatalogProvider } from '../../../fp-b2b-shared/components/ProductCatalogProvider/ProductCatalogProvider'
import { DIALOG_PRODUCT_CART, DialogProductCart, type DialogProductCartProps } from '../../components/DialogProductCart'
import { useStmBrandStore } from '../../hooks'
import { useBrandNewArrivalProducts } from '../hooks/use-brand-new-arrival-products'
import { type ProductCardEntry } from '../types/product-card-entry'
import { ProductCard } from './ProductCard'
import { ProductList } from './ProductList'

const BANNER_WIDTH_DESKTOP = 246
const BANNER_HEIGHT_DESKTOP = 282
const BANNER_ASPECT_RATIO_MOBILE = '300 / 400'
const BANNER_HEIGHT_MOBILE = 282

const styles = stylex.create({
  bodyRow: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  bannerDesktop: {
    flexShrink: 0,
    width: BANNER_WIDTH_DESKTOP,
  },
  bannerMobile: {
    aspectRatio: BANNER_ASPECT_RATIO_MOBILE,
    flexShrink: 0,
    height: BANNER_HEIGHT_MOBILE,
  },
  productPanel: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
  },
})


interface BrandNewArrivalSectionContentProps {
  campaign: CrossBrandMallHomeCampaign
  currency: string
  basePath: string
}

export const BrandNewArrivalSectionContentSkeleton = () => {
  const isMobile = useResponsive('mobile')

  const mobileBannerSkeleton = (
    <Box rootStyle={styles.bannerMobile}>
      <Skeleton width="100%" height="100%" />
    </Box>
  )

  return (
    <HStack p={3} rootStyle={styles.bodyRow} gap={3} alignItems="stretch">
      {!isMobile && (
        <Box rootStyle={styles.bannerDesktop}>
          <Skeleton width="100%" height={BANNER_HEIGHT_DESKTOP} />
        </Box>
      )}
      <Box rootStyle={styles.productPanel}>
        <ProductList
          isLoading={true}
          leadingSlot={isMobile ? mobileBannerSkeleton : undefined}
          hasSubtitleChips
        />
      </Box>
    </HStack>
  )
}

export const BrandNewArrivalSectionContent = (props: BrandNewArrivalSectionContentProps) => {
  const { campaign, currency, basePath } = props

  const { productCardEntries, isProductsLoading } = useBrandNewArrivalProducts({ campaign })
  const isMobile = useResponsive('mobile')

  const brandInfo = useStmBrandStore(state =>
    state.brands.find(brand => brand.campaignCode === campaign.code),
  )
  const dialogKey = `${DIALOG_PRODUCT_CART}_NEW_ARRIVAL_${campaign.code}`
  const { openDialog } = useDialog<DialogProductCartProps>(dialogKey)
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaign.code })
  const { sendCrossBrandAddToCartEvent } = useStmAnalyticsEvent({ userId: userInfo?.platform_uid ?? '' })

  if (!brandInfo) return null

  const handleOpenCartDialog = (entry: ProductCardEntry) => {
    sendCrossBrandAddToCartEvent({ projectCode: campaign.project_code, sectionName: StmSectionName.NEW_ARRIVALS })
    openDialog({
      title: entry.title,
      thumbnail: entry.imageUrl,
      variations: entry.variations,
      basePath,
    })
  }

  const bannerDeviceType = isMobile ? CrossBrandMallNewArrivalImageDeviceType.MOBILE : CrossBrandMallNewArrivalImageDeviceType.PC
  const bannerUrl = campaign.new_arrival_image.find(image => image.device_type === bannerDeviceType)?.url ?? null

  const mobileBanner = isMobile && bannerUrl !== null
    ? (
      <Box rootStyle={styles.bannerMobile}>
        <Image src={bannerUrl} alt="新品上市" width="100%" height="100%" objectFit="cover" />
      </Box>
    )
    : undefined

  return (
    <FpB2bProductCatalogProvider
      platform={Platform.STM}
      campaignId={brandInfo.campaignId}
      campaignCode={brandInfo.campaignCode}
      projectCode={brandInfo.projectCode}
      countryCode={brandInfo.countryCode}
      currency={currency}
    >
      <HStack p={3} rootStyle={styles.bodyRow} gap={3} alignItems="stretch">
        {!isMobile && bannerUrl !== null && (
          <Box rootStyle={styles.bannerDesktop}>
            <Image src={bannerUrl} alt="新品上市" width="100%" height="100%" objectFit="cover" />
          </Box>
        )}
        <Box rootStyle={styles.productPanel}>
          <ProductList
            isLoading={isProductsLoading}
            leadingSlot={mobileBanner}
            hasSubtitleChips
          >
            {productCardEntries.map(entry => (
              <ProductCard key={entry.key} entry={entry} onSelect={handleOpenCartDialog} />
            ))}
          </ProductList>
        </Box>
      </HStack>
      <DialogProductCart dialogKey={dialogKey} />
    </FpB2bProductCatalogProvider>
  )
}
